import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { KEY_CODES } from '@/main/app.constants';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, Checkbox as QomponentCheckbox } from '@seeqdev/qomponents';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { TooltipPosition } from '@seeqdev/qomponents';

interface CheckboxProps {
  id?: string;
  isChecked: boolean;
  label?: string | React.ReactNode;
  extraLabel?: React.ReactElement;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  value?: string | number | boolean;
  type?: 'radio' | 'checkbox';
  disabled?: boolean;
  classes?: string;
  tooltip?: string;
  tooltipPlacement?: TooltipPosition;
  isTooltipSimple?: boolean;
  description?: string;
  skipMemo?: boolean;
  descriptionClasses?: string;
  onHelpIconClick?: () => void;
  flexAlign?: 'start' | 'center' | 'bottom';
  extraLabelClassNames?: string;
  labelXstyle?: StyleXStyles;
  xstyle?: StyleXStyles;
  /** Opt into StyleX layout ownership; legacy callers retain their print layout hooks. */
  layoutXstyle?: StyleXStyles;
  fieldXstyle?: StyleXStyles;
  checkboxXstyle?: StyleXStyles;
}

export const Checkbox = (props: CheckboxProps) => {
  const {
    onChange = _.noop,
    isChecked,
    label,
    extraLabel,
    id,
    disabled,
    type = 'checkbox',
    classes,
    tooltip,
    tooltipPlacement = 'top',
    isTooltipSimple = false,
    description,
    value,
    descriptionClasses = 'ml20 pl2 mtn5 width-200 font-size-smaller',
    onHelpIconClick,
    onClick,
    flexAlign = 'start',
    extraLabelClassNames,
    labelXstyle,
    xstyle,
    layoutXstyle,
    fieldXstyle,
    checkboxXstyle,
  } = props;

  const { t } = useTranslation();

  const rootStyles = stylex.props(
    layoutXstyle && styles.root,
    layoutXstyle && flexAlign === 'center' && styles.centered,
    layoutXstyle && flexAlign === 'bottom' && styles.bottomAligned,
    xstyle,
    layoutXstyle,
  );

  return (
    <span
      {...rootStyles}
      className={clsx(
        !layoutXstyle && 'flexColumnContainer',
        classes,
        rootStyles.className,
        isChecked ? 'specChecked' : 'specNotChecked',
        {
          flexAlignCenter: !layoutXstyle && flexAlign === 'center',
          flexAlignBottom: !layoutXstyle && flexAlign === 'bottom',
          flexAlignStart: !layoutXstyle && flexAlign === 'start',
        },
      )}
      data-testid={id}>
      <div className={clsx('flexColumnContainer sq-checkbox')}>
        <QomponentCheckbox
          value={value as string | number | undefined}
          onKeyDown={(e) => e.keyCode === KEY_CODES.ENTER && e.currentTarget.click()}
          onChange={onChange}
          onClick={onClick}
          checked={isChecked}
          type={type}
          id={id}
          testId={`${id}-input`}
          disabled={disabled}
          label={typeof label === 'string' ? t(label) : label}
          tooltip={tooltip && t(tooltip)}
          tooltipPlacement={tooltipPlacement}
          extraLabelClassNames={extraLabelClassNames}
          labelXstyle={labelXstyle}
          xstyle={fieldXstyle}
          inputXstyle={checkboxXstyle}
        />
        {extraLabel}
      </div>
      {!isTooltipSimple && tooltip && (
        <Icon
          icon={faQuestionCircle}
          size="lg"
          tooltip={t(tooltip)}
          tooltipPlacement={tooltipPlacement}
          extraClassNames="pl5 pt1 tw:leading-none!"
          onClick={onHelpIconClick}
          testId={`${id}Tooltip`}
        />
      )}
      {description && <div className={clsx('sq-fairly-dark-gray tw:italic', descriptionClasses)}>{t(description)}</div>}
    </span>
  );
};

const styles = stylex.create({
  root: {
    display: { 'default': 'flex', '@media print': 'block' },
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  centered: { alignItems: 'center' },
  bottomAligned: { marginTop: 'auto' },
});
